import React, { useState } from 'react';
import { Menu, User, Sun, Moon, Monitor, Contrast, Palette, MoreVertical } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { HARRY_AVATAR_SRC } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useShell } from '@/contexts/shell-context';
import { useClient } from '@/context/client-context';
import { useAdmin } from '@/contexts/AdminContext';
import {
  useTheme,
  getNextBasicTheme,
  BASIC_THEME_LABELS,
  type Theme,
} from '@/components/theme-provider';
import { useSettings } from '@/components/settings/lib/settings-context';
import { SelectClientDialog } from '@/components/select-client-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { clientDisplayLabel } from '@/lib/clients/roster-fields';
import { HelpHeaderButton } from '@/components/help/HelpHeaderButton';

export interface ProductHeaderProps {
  sectionTitle?: string;
  showAddButton?: boolean;
  onAddClick?: () => void;
  className?: string;
}

const NEXT_THEME_ICON: Record<Theme, React.ReactNode> = {
  light: <Sun className="h-5 w-5" />,
  dark: <Moon className="h-5 w-5" />,
  system: <Monitor className="h-5 w-5" />,
  'high-contrast': <Contrast className="h-5 w-5" />,
  glass: <Palette className="h-5 w-5" />,
};

/**
 * Product chrome header redesigned according to visual specs:
 * Left: Menu toggle · Coach circular avatar · Client selection button (User icon + client.name)
 * Right: Help button (screens with Help entries) · Appearance theme cycle button (Light → Dark → System → High Contrast → Glass).
 */
export function ProductHeader({
  className,
}: ProductHeaderProps) {
  const navigate = useNavigate();
  const { toggleNav, navExpanded, navOpen, isMobile, openCoach, coachOpen } = useShell();
  const { settings } = useSettings();
  const { client, setClient } = useClient();
  const coachChatEnabled = settings.experimentalFeaturesEnabled === true;
  const { theme, setTheme } = useTheme();
  const [isClientDialogOpen, setIsClientDialogOpen] = useState(false);
  const menuExpanded = isMobile ? navOpen : navExpanded;
  const nextTheme = getNextBasicTheme(theme);

  const handleStartAssessment = () => {
    if (!client) return;
    navigate(`/clients/${client.id}/assessment/new`);
  };

  const handlePostureAnalysis = () => {
    if (!client) return;
    // Mirrors clients-page-content.tsx's handleStartPostureAnalysis: (re-)assert the
    // app-wide client context before navigating so downstream flows agree on the client.
    setClient(client);
    navigate('/posture-analysis');
  };

  const handleOpenClient360 = () => {
    if (!client) return;
    navigate(`/clients/${client.id}`);
  };

  const handleDeselect = () => {
    setClient(null);
  };

  let isAdminMode = false;
  let isAuthorizedAdmin = false;
  try {
    const admin = useAdmin();
    isAdminMode = admin.isAdminMode;
    isAuthorizedAdmin = admin.isAuthorizedAdmin;
  } catch (error) {
    // Ignore context error if rendered outside AdminProvider
  }

  return (
    <header
      className={cn(
        'sticky top-0 z-50 flex h-16 items-center justify-between gap-2 border-b bg-background px-3 py-2 supports-[backdrop-filter]:bg-background/80 sm:px-4 lg:px-5',
        className
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2.5 lg:gap-3">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="inline-flex h-11 w-11 shrink-0 rounded-lg"
          aria-label="Open navigation menu"
          aria-expanded={menuExpanded}
          aria-controls="app-sidebar"
          onClick={toggleNav}
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Coach / Harry Avatar — always visible; opens chat sheet only when experimental features are on */}
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn(
            'h-10 w-10 overflow-hidden rounded-full p-0 shrink-0 border border-border',
            coachChatEnabled && coachOpen && 'ring-2 ring-primary ring-offset-2 ring-offset-background'
          )}
          aria-label={coachChatEnabled ? 'Ask Harry — open chat' : 'Harry'}
          aria-expanded={coachChatEnabled ? coachOpen : undefined}
          title={coachChatEnabled ? 'Ask Harry' : 'Harry'}
          onClick={coachChatEnabled ? openCoach : undefined}
        >
          <img src={HARRY_AVATAR_SRC} alt="" className="h-full w-full object-cover object-top" width={40} height={40} />
        </Button>

        {/* Active Client Selection Trigger — reflects the shared client-context (useClient()),
            the same context set by roster row selection, Client 360 visits, and this dialog. */}
        <div className="flex min-w-0 items-center gap-0.5">
          <SelectClientDialog open={isClientDialogOpen} onOpenChange={setIsClientDialogOpen}>
            <button
              type="button"
              className="inline-flex min-w-0 items-center gap-2 h-10 px-2.5 text-sm font-semibold text-foreground hover:bg-accent hover:text-accent-foreground rounded-lg transition-colors duration-150"
              aria-label={client ? `Selected client: ${clientDisplayLabel(client)}` : 'Select client'}
            >
              <User className="h-4.5 w-4.5 text-muted-foreground shrink-0" />
              <span className="line-clamp-2 whitespace-normal text-left leading-tight max-w-[9rem] sm:max-w-[180px] sm:truncate sm:whitespace-nowrap lg:max-w-[240px]">
                {client ? clientDisplayLabel(client) : 'Select Client'}
              </span>
            </button>
          </SelectClientDialog>

          {client && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 shrink-0 rounded-lg"
                  aria-label={`More actions for ${clientDisplayLabel(client)}`}
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onSelect={handleOpenClient360}>Client 360</DropdownMenuItem>
                <DropdownMenuItem onSelect={handleStartAssessment}>Start assessment</DropdownMenuItem>
                <DropdownMenuItem onSelect={handlePostureAnalysis}>Posture analysis</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={handleDeselect}>Deselect</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <HelpHeaderButton />
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-10 w-10 rounded-lg shrink-0"
          aria-label={`Switch to ${BASIC_THEME_LABELS[nextTheme]} appearance`}
          title={`Theme: ${BASIC_THEME_LABELS[theme]} — click for ${BASIC_THEME_LABELS[nextTheme]}`}
          onClick={() => setTheme(nextTheme)}
        >
          {NEXT_THEME_ICON[nextTheme]}
        </Button>
      </div>
    </header>
  );
}
